// src/subapps/shipments/shipments/psm-shipments/hooks/useShipmentDetails.js

import { useEffect, useMemo, useState } from 'react'
import { batchIdToProductId } from '../../../common/utils' // adjust path
import get from 'lodash/get'
import invoke from 'lodash/invoke'

/**
 * Hook for holding logic to fetch shipments details (Currently used in InboundShipments and StockTransfers components ).
 *
 * @param {Object} params
 * @param {Object} params.api - FsApi
 * @param {Object} params.user - Current user.
 * @param {Object} params.shipment
 * @param {{ shipment: { viewed: (payload: { shipment: Object }) => void }}} params.trackingHandler
 *
 * @returns {{ productsMap: Map<string, Object>, loading: boolean, error: Error|null }}
 */
export default function useShipmentDetails ({ api, user, shipment, trackingHandler }) {
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)
  const [productsMap, setProductsMap] = useState(new Map())

  const productIds = useMemo(() => {
    const counts = get(shipment, 'counts', {})
    // ensure uniqueness to avoid redundant fetches
    const ids = Object.keys(counts).map(batchIdToProductId)
    return Array.from(new Set(ids))
  }, [shipment])

  useEffect(() => {
    let mounted = true
    ;(async () => {
      try {
        // loadPickList is dispatched by the containers (kept there if you prefer),
        // but you can also accept it here as a param and await it.
        const products = productIds.length ? await api.product.getByIds(productIds) : []
        const map = products.reduce((m, p) => m.set(p._id, p), new Map())
        if (!mounted) return
        setProductsMap(map)

        invoke(trackingHandler, 'shipment.viewed', { shipment })
      } catch (e) {
        if (!mounted) return
        setError(e)
      } finally {
        if (mounted) setLoading(false)
      }
    })()
    return () => { mounted = false }
  }, [api, productIds, shipment, trackingHandler])

  return { productsMap, loading, error }
}
